import React from 'react';
import { Modal } from 'react-bootstrap';

function renderInlineFormatting(text) {
  return String(text)
    .split(/(\*\*[^*]+\*\*)/g)
    .filter(Boolean)
    .map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={index}>{part.slice(2, -2)}</strong>;
      }
      return part;
    });
}

function renderFeatureDescription(description) {
  if (!description) return <p>Feature details unavailable</p>;

  const blocks = String(description)
    .split(/\n\s*\n/g)
    .map((block) => block.trim())
    .filter(Boolean);

  if (blocks.length === 0) return <p>Feature details unavailable</p>;

  return (
    <div className="feature-modal-description">
      {blocks.map((block, index) => {
        const lines = block.split('\n').map((line) => line.trim()).filter(Boolean);
        const isList = lines.length > 0 && lines.every((line) => /^[-*]\s+/.test(line));
        if (isList) {
          return (
            <ul key={index}>
              {lines.map((line, lineIndex) => (
                <li key={lineIndex}>{renderInlineFormatting(line.replace(/^[-*]\s+/, ''))}</li>
              ))}
            </ul>
          );
        }

        const isHeading = lines.length === 1 && /^\*\*[^*]+\*\*$/.test(lines[0]);
        if (isHeading) {
          return <p key={index} className="feature-modal-description__heading">{renderInlineFormatting(lines[0])}</p>;
        }

        return <p key={index}>{renderInlineFormatting(lines.join('\n'))}</p>;
      })}
    </div>
  );
}

/**
 * Compact details sheet for a feature card. `summary` (optional) mirrors the card:
 * { icon, sourceParts: string[], chips: [{ text, tone }] } where tone matches the tile status tones.
 */
export default function FeatureModal({ show, onHide, feature, summary }) {
  if (!show || !feature) return null;
  const description = Array.isArray(feature.desc)
    ? feature.desc.join('\n\n')
    : (feature.description || feature.desc);
  const sourceParts = summary?.sourceParts || [];
  const chips = summary?.chips || [];
  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      className="dnd-modal modern-modal feature-detail-modal"
      dialogClassName="feature-sheet-dialog"
      aria-labelledby="feature-detail-modal-title"
    >
      <div className="feature-sheet">
        <div className="feature-sheet-header">
          {summary?.icon && <div className="ability-card-icon feature-sheet-icon" aria-hidden="true">{summary.icon}</div>}
          <div className="feature-sheet-heading">
            <h2 id="feature-detail-modal-title" className="feature-sheet-title">{feature.name}</h2>
            {sourceParts.length > 0 && (
              <div className="feature-sheet-meta">
                {sourceParts.map((part) => <span key={part}>{part}</span>)}
              </div>
            )}
          </div>
          <button type="button" className="feature-sheet-close" aria-label="Close" onClick={onHide}>
            <i className="fa-solid fa-xmark" aria-hidden="true" />
          </button>
        </div>
        <div className="feature-sheet-body">
          {chips.length > 0 && (
            <div className="feature-sheet-chips">
              {chips.map((chip) => (
                <span key={chip.text} className={`ability-tile-status ability-tile-status--${chip.tone || 'plain'}`}>{chip.text}</span>
              ))}
            </div>
          )}
          {typeof feature.renderDetails === 'function' ? (
            feature.renderDetails()
          ) : (
            renderFeatureDescription(description)
          )}
        </div>
      </div>
    </Modal>
  );
}
